/**
 * The named sound effects.
 *
 * All of it is synthesised — no samples, nothing to load. The palette is as
 * disciplined as the visual one: everything sits in the key of D, so when a
 * wild, a 21, a five-card and a streak all land on the same play they arpeggiate
 * one chord instead of fighting. Card and UI sounds are deliberately dull and
 * quiet; the game is played in long sessions and the thud fires on every move.
 *
 * Levels are conservative on purpose. The limiter in {@link SynthEngine} is a
 * safety net, not a mixing tool.
 */

import {
  clamp,
  harmonicWave,
  mtof,
  noiseBurst,
  rand,
  tone,
  type MixGroup,
  type SynthBus,
} from '@/audio/synth';

/** Common options every cue accepts. */
export interface CueOptions {
  /** Absolute context time. Defaults to `bus.now()`. */
  when?: number;
  /** Linear level scale, 1 = as designed. */
  level?: number;
  /** -1 (left) .. 1 (right). */
  pan?: number;
}

/* ------------------------------------------------------------------- tuning -- */

/** Tonic of the whole sound set: D4. Everything is a scale degree from here. */
const D4 = 62;
const D5 = D4 + 12;
const A3 = 57;
const D3 = 50;

/** D major pentatonic degrees used by the streak ladder. */
const STREAK_STEPS = [0, 3, 5, 7, 12] as const;

const BONUS: MixGroup = 'bonus';
const CARD: MixGroup = 'card';
const UI: MixGroup = 'ui';

/* -------------------------------------------------------------------- waves -- */

/** Clean, glassy — used for the 21. */
const glassWave = (bus: SynthBus): PeriodicWave =>
  harmonicWave(bus.ctx, 'glass', [1, 0.3, 0.11, 0.045, 0.018]);

/** Fuller, more metal in it — the five-card payout. */
const bellWave = (bus: SynthBus): PeriodicWave =>
  harmonicWave(bus.ctx, 'bell', [1, 0.54, 0.31, 0.19, 0.11, 0.06, 0.03]);

/** Band-limited saw. Warm rather than buzzy; the wild's rising pad. */
const softSawWave = (bus: SynthBus): PeriodicWave =>
  harmonicWave(bus.ctx, 'softSaw', [1, 0.5, 0.33, 0.25, 0.2, 0.16, 0.13, 0.1]);

/** Almost a sine with a little wood in it — the bust body. */
const dullWave = (bus: SynthBus): PeriodicWave =>
  harmonicWave(bus.ctx, 'dull', [1, 0.16, 0.05]);

/* ------------------------------------------------------------------- helper -- */

interface BellOptions {
  when: number;
  midi: number;
  level: number;
  decay: number;
  wave: PeriodicWave;
  send?: number;
  pan?: number;
  /** Rolls the top off so high notes never get shrill. */
  tilt?: number;
}

/** One struck note. A single oscillator with a tailored spectrum. */
function struck(bus: SynthBus, o: BellOptions): number {
  const freq = mtof(o.midi);
  return tone(bus, {
    freq,
    wave: o.wave,
    attack: 0.004,
    decay: o.decay,
    level: o.level,
    when: o.when,
    group: BONUS,
    send: o.send ?? 0.3,
    pan: o.pan ?? 0,
    filter: { type: 'lowpass', freq: clamp(freq * (o.tilt ?? 6), 800, 15000), q: 0.5 },
  });
}

/* -------------------------------------------------------------------- cards -- */

/**
 * A card leaving the stock: a short paper-on-felt whoosh. Band-passed noise
 * falling in pitch, with a breath of air on the front so it has an onset.
 */
export function cardDeal(bus: SynthBus, o: CueOptions = {}): void {
  const when = o.when ?? bus.now();
  const level = o.level ?? 1;
  const pan = o.pan ?? rand(-0.18, 0.18);

  noiseBurst(bus, {
    kind: 'pink',
    when,
    attack: 0.008,
    decay: rand(0.1, 0.14),
    level: 0.68 * level,
    rate: rand(0.94, 1.08),
    group: CARD,
    pan,
    send: 0.1,
    filter: { type: 'bandpass', freq: rand(1500, 1850), q: 0.9, sweepTo: 620, sweepTime: 0.13 },
    filter2: { type: 'highpass', freq: 260, q: 0.6 },
  });

  // A touch of air right at the top so the whoosh has a leading edge.
  noiseBurst(bus, {
    kind: 'white',
    when,
    attack: 0.003,
    decay: 0.045,
    level: 0.17 * level,
    group: CARD,
    pan,
    send: 0.08,
    filter: { type: 'bandpass', freq: 3600, q: 1.1, sweepTo: 2100, sweepTime: 0.05 },
  });
}

/**
 * A card arriving on a pile. This fires on every single move, so it is soft,
 * dark and slightly different every time — pitch, decay, level and noise
 * character all wander a little so a fast run never machine-guns.
 */
export function cardLand(bus: SynthBus, o: CueOptions = {}): void {
  const when = o.when ?? bus.now();
  const level = (o.level ?? 1) * rand(0.86, 1.06);
  const pan = o.pan ?? 0;
  const body = rand(132, 152);

  // Body: a soft pitched drop. Not a kick — it barely has a pitch, just weight.
  tone(bus, {
    freq: body,
    wave: dullWave(bus),
    glideTo: body * 0.68,
    glideTime: 0.075,
    attack: 0.004,
    decay: rand(0.15, 0.2),
    level: 0.195 * level,
    when,
    group: CARD,
    pan,
    send: 0.1,
    filter: { type: 'lowpass', freq: 900, q: 0.7 },
  });

  // Felt: the contact itself, dark and very short.
  noiseBurst(bus, {
    kind: 'pink',
    when,
    attack: 0.002,
    decay: rand(0.055, 0.085),
    level: 0.105 * level,
    rate: rand(0.85, 1.15),
    group: CARD,
    pan,
    send: 0.12,
    filter: { type: 'lowpass', freq: rand(760, 1050), q: 0.9, sweepTo: 340, sweepTime: 0.07 },
  });

  // Edge: a hint of card lacquer so the thud reads as cardboard, not a drum.
  noiseBurst(bus, {
    kind: 'white',
    when: when + 0.004,
    attack: 0.0015,
    decay: 0.022,
    level: 0.034 * level,
    group: CARD,
    pan,
    send: 0.06,
    filter: { type: 'bandpass', freq: rand(2200, 2900), q: 1.4 },
  });
}

/** Turning a card over: a crisp flick, gone almost before you notice it. */
export function cardFlip(bus: SynthBus, o: CueOptions = {}): void {
  const when = o.when ?? bus.now();
  const level = o.level ?? 1;
  const pan = o.pan ?? rand(-0.12, 0.12);

  noiseBurst(bus, {
    kind: 'white',
    when,
    attack: 0.0015,
    decay: rand(0.045, 0.062),
    level: 0.25 * level,
    rate: rand(0.95, 1.1),
    group: CARD,
    pan,
    send: 0.1,
    filter: { type: 'bandpass', freq: 1300, q: 1.2, sweepTo: 4200, sweepTime: 0.045 },
    filter2: { type: 'highpass', freq: 700, q: 0.5 },
  });

  tone(bus, {
    freq: rand(2500, 2900),
    type: 'triangle',
    attack: 0.001,
    decay: 0.028,
    level: 0.088 * level,
    when,
    group: CARD,
    pan,
    send: 0.08,
  });
}

/* ------------------------------------------------------------------ bonuses -- */

/**
 * Exactly 21. Bright, precise, resolved: a clean fifth (D5 + A5) struck
 * together, with the octave arriving a hair later for sparkle.
 */
export function twentyOne(bus: SynthBus, o: CueOptions = {}): void {
  const when = o.when ?? bus.now();
  const level = o.level ?? 1;
  const pan = o.pan ?? 0;
  const wave = glassWave(bus);

  struck(bus, { when, midi: D5, level: 0.22 * level, decay: 0.62, wave, send: 0.3, pan: pan - 0.06 });
  struck(bus, { when, midi: D5 + 7, level: 0.15 * level, decay: 0.7, wave, send: 0.32, pan: pan + 0.06 });
  struck(bus, { when: when + 0.055, midi: D5 + 12, level: 0.084 * level, decay: 0.8, wave, send: 0.38, pan });
}

/**
 * Five cards under 21 — the bigger payout, so this is fuller than the 21:
 * a low root for body under a fast rising D major arpeggio, with a breath of
 * air over the top.
 */
export function fiveCard(bus: SynthBus, o: CueOptions = {}): void {
  const when = o.when ?? bus.now();
  const level = o.level ?? 1;
  const pan = o.pan ?? 0;
  const wave = bellWave(bus);

  tone(bus, {
    freq: mtof(D4 - 12),
    wave: dullWave(bus),
    attack: 0.008,
    decay: 0.75,
    level: 0.19 * level,
    when,
    group: BONUS,
    pan,
    send: 0.2,
    filter: { type: 'lowpass', freq: 520, q: 0.7 },
  });

  const arp = [D4, D4 + 4, D4 + 7, D5];
  for (let i = 0; i < arp.length; i++) {
    struck(bus, {
      when: when + i * 0.055,
      midi: arp[i]!,
      level: (0.22 - i * 0.015) * level,
      decay: 0.5 + i * 0.14,
      wave,
      send: 0.3 + i * 0.03,
      pan: pan + (i - 1.5) * 0.05,
      tilt: 5,
    });
  }

  noiseBurst(bus, {
    kind: 'white',
    when: when + 0.16,
    attack: 0.05,
    decay: 0.45,
    level: 0.039 * level,
    group: BONUS,
    pan,
    send: 0.5,
    filter: { type: 'highpass', freq: 5200, q: 0.5 },
  });
}

/**
 * The wild. It is a rescue card, so the sound is tension releasing: a pad
 * gliding up an octave through an opening resonant filter, with a high partial
 * blooming late.
 */
export function wild(bus: SynthBus, o: CueOptions = {}): void {
  const when = o.when ?? bus.now();
  const level = o.level ?? 1;
  const pan = o.pan ?? 0;
  const wave = softSawWave(bus);
  const from = mtof(A3);
  const to = mtof(A3 + 12);

  for (const detune of [-7, 7]) {
    tone(bus, {
      freq: from,
      wave,
      detune,
      glideTo: to,
      glideTime: 0.36,
      attack: 0.035,
      hold: 0.06,
      decay: 0.5,
      level: 0.14 * level,
      when,
      group: BONUS,
      pan: pan + detune * 0.014,
      send: 0.42,
      filter: { type: 'lowpass', freq: 420, q: 3.2, sweepTo: 4400, sweepTime: 0.38 },
    });
  }

  // The release: a clean high fifth blooming once the glide has landed.
  tone(bus, {
    freq: mtof(A3 + 24),
    wave: glassWave(bus),
    attack: 0.1,
    decay: 0.62,
    level: 0.12 * level,
    when: when + 0.26,
    group: BONUS,
    pan,
    send: 0.5,
    filter: { type: 'lowpass', freq: 6500, q: 0.5 },
  });

  noiseBurst(bus, {
    kind: 'pink',
    when,
    attack: 0.22,
    decay: 0.16,
    level: 0.063 * level,
    group: BONUS,
    pan,
    send: 0.45,
    filter: { type: 'bandpass', freq: 900, q: 0.8, sweepTo: 5200, sweepTime: 0.34 },
  });
}

/**
 * Bust. Dull, damped, short — a dropped weight. There is nothing above 700 Hz
 * in it, because punishing the player's ears for a mistake is cheap.
 */
export function bust(bus: SynthBus, o: CueOptions = {}): void {
  const when = o.when ?? bus.now();
  const level = o.level ?? 1;
  const pan = o.pan ?? 0;

  tone(bus, {
    freq: 152,
    wave: dullWave(bus),
    glideTo: 88,
    glideTime: 0.2,
    attack: 0.006,
    decay: 0.3,
    level: 0.24 * level,
    when,
    group: BONUS,
    pan,
    send: 0.14,
    filter: { type: 'lowpass', freq: 480, q: 0.9 },
  });

  // A muted minor second underneath — the sour note, felt more than heard.
  tone(bus, {
    freq: mtof(D3 + 1),
    type: 'sine',
    attack: 0.01,
    decay: 0.34,
    level: 0.064 * level,
    when: when + 0.01,
    group: BONUS,
    pan,
    send: 0.16,
    filter: { type: 'lowpass', freq: 400, q: 0.7 },
  });

  noiseBurst(bus, {
    kind: 'pink',
    when,
    attack: 0.002,
    decay: 0.11,
    level: 0.128 * level,
    rate: 0.8,
    group: BONUS,
    pan,
    send: 0.1,
    filter: { type: 'lowpass', freq: 300, q: 1.1, sweepTo: 140, sweepTime: 0.1 },
  });
}

/**
 * Streak, levels 1..5 (the 2x..6x callouts). A two-note pip climbing a D major
 * pentatonic ladder, getting brighter and wider as it goes, so 6x is audibly
 * the top rung.
 */
export function streak(bus: SynthBus, streakLevel: number, o: CueOptions = {}): void {
  const index = clamp(Math.round(streakLevel) - 1, 0, STREAK_STEPS.length - 1);
  const when = o.when ?? bus.now();
  const level = o.level ?? 1;
  const pan = o.pan ?? 0;
  const wave = glassWave(bus);
  const root = D5 + STREAK_STEPS[index]!;
  const heat = index / (STREAK_STEPS.length - 1);

  struck(bus, {
    when,
    midi: root,
    level: (0.19 + 0.041 * heat) * level,
    decay: 0.34 + 0.12 * heat,
    wave,
    send: 0.26 + 0.08 * heat,
    pan: pan - 0.08,
    tilt: 4 + heat * 3,
  });

  struck(bus, {
    when: when + 0.075,
    midi: root + 7,
    level: (0.143 + 0.038 * heat) * level,
    decay: 0.42 + 0.2 * heat,
    wave,
    send: 0.3 + 0.1 * heat,
    pan: pan + 0.08,
    tilt: 4 + heat * 3,
  });

  // Top of the ladder gets an octave of extra shine — only the 6x earns it.
  if (index >= STREAK_STEPS.length - 1) {
    struck(bus, {
      when: when + 0.15,
      midi: root + 12,
      level: 0.095 * level,
      decay: 0.75,
      wave,
      send: 0.45,
      pan,
      tilt: 6,
    });
  }
}

/* -------------------------------------------------------------------- clock -- */

/**
 * The final-fifteen pulse. One per second, very soft, sitting back in the room.
 * It only starts climbing in the last five seconds, so the first ten reads as a
 * heartbeat rather than an alarm.
 */
/**
 * The level-up chime: three glass notes rising, ~0.8 s.
 *
 * Built to be told apart from `streak()` at a glance, because the two can land
 * within half a second of each other and mean different things. The streak is
 * two notes 75 ms apart on a rising ladder, struck hard — it is money arriving.
 * This is three notes 90 ms apart on a fixed minor-third-then-major-third
 * shape, struck softly with a long tail and a heavy reverb send: a milestone
 * being noted, not a payout.
 *
 * No drum, no impact, no low end. Every percussive cue in this game means
 * something landed on the table; a level lands on nothing.
 *
 * The arpeggio is the same at every level. A chime that climbed with the level
 * would be a ladder, and a ladder is what the streak already is — two ladders
 * in the same ear is how a mix stops being legible.
 */
export function levelUp(bus: SynthBus, milestone = false, o: CueOptions = {}): void {
  const when = o.when ?? bus.now();
  const level = o.level ?? 1;
  const pan = o.pan ?? 0;
  const wave = glassWave(bus);

  // F, A flat, C — a minor triad, rising. Cool rather than triumphant; a major
  // arpeggio here read as a fanfare for something that pays nothing.
  const root = D5 + 3;
  const steps = [0, 3, 7];

  steps.forEach((step, i) => {
    struck(bus, {
      when: when + i * 0.09,
      midi: root + step + (milestone ? 12 : 0),
      // Under the music on purpose, and quieter than any scoring cue.
      level: (0.115 + (milestone ? 0.03 : 0)) * level,
      decay: 0.5 + i * 0.12,
      wave,
      // Wetter than the streak pips: the tail is what makes it read as glass
      // rather than as another pip.
      send: 0.42,
      pan: pan + (i - 1) * 0.07,
      tilt: 7,
    });
  });

  if (milestone) {
    // One octave below the root, very quiet, to give every fifth level a floor
    // the plain ones do not have. Still struck, still glass — not a drum.
    struck(bus, {
      when: when + 0.09,
      midi: root - 12,
      level: 0.07 * level,
      decay: 0.9,
      wave,
      send: 0.5,
      pan,
      tilt: 5,
    });
  }
}

export function tickWarning(bus: SynthBus, secondsRemaining: number, o: CueOptions = {}): void {
  const when = o.when ?? bus.now();
  const seconds = clamp(secondsRemaining, 1, 15);
  const urgency = seconds <= 5 ? (5 - seconds) / 4 : 0;
  const midi = 69 + Math.round(urgency * 4);
  const level = (o.level ?? 1) * (0.068 + urgency * 0.045);

  tone(bus, {
    freq: mtof(midi),
    type: 'sine',
    attack: 0.014,
    decay: 0.2 + urgency * 0.06,
    level,
    when,
    group: UI,
    pan: o.pan ?? 0,
    send: 0.24,
    filter: { type: 'lowpass', freq: 1900 + urgency * 900, q: 0.6 },
  });

  // A ghost of the octave below gives the pulse a body without adding volume.
  tone(bus, {
    freq: mtof(midi - 12),
    type: 'sine',
    attack: 0.012,
    decay: 0.14,
    level: level * 0.5,
    when,
    group: UI,
    pan: o.pan ?? 0,
    send: 0.15,
  });
}

/* ---------------------------------------------------------------- end of it -- */

/**
 * End of round: a plain authentic cadence, A major into D major, played soft
 * and let ring. Resolved, not triumphant — it plays after a bust-out too.
 */
export function gameOver(bus: SynthBus, o: CueOptions = {}): void {
  const when = o.when ?? bus.now();
  const level = o.level ?? 1;
  const pan = o.pan ?? 0;
  const wave = bellWave(bus);

  const dominant = [A3, A3 + 16, A3 + 19];
  for (let i = 0; i < dominant.length; i++) {
    struck(bus, {
      when: when + i * 0.012,
      midi: dominant[i]!,
      level: (0.145 - i * 0.023) * level,
      decay: 0.9,
      wave,
      send: 0.4,
      pan: pan + (i - 1) * 0.12,
      tilt: 4,
    });
  }

  const tonicChord = [D3, D3 + 12, D3 + 16, D3 + 24];
  for (let i = 0; i < tonicChord.length; i++) {
    struck(bus, {
      when: when + 0.44 + i * 0.014,
      midi: tonicChord[i]!,
      level: (0.164 - i * 0.023) * level,
      decay: 1.5 + i * 0.15,
      wave,
      send: 0.44,
      pan: pan + (i - 1.5) * 0.09,
      tilt: 4,
    });
  }
}

/**
 * Perfect game: stock empty and all four piles clear. The best sound in the
 * game — a rising D major arpeggio into a wide, ringing tonic chord with a
 * gold shimmer over it. Still restrained; it just has more air and more time.
 */
export function perfectGame(bus: SynthBus, o: CueOptions = {}): void {
  const when = o.when ?? bus.now();
  const level = o.level ?? 1;
  const pan = o.pan ?? 0;
  const wave = bellWave(bus);

  const arp = [D4, D4 + 4, D4 + 7, D4 + 12, D4 + 16];
  for (let i = 0; i < arp.length; i++) {
    struck(bus, {
      when: when + i * 0.062,
      midi: arp[i]!,
      level: (0.166 + i * 0.008) * level,
      decay: 0.55 + i * 0.1,
      wave,
      send: 0.34,
      pan: pan + (i - 2) * 0.06,
      tilt: 5,
    });
  }

  const chordAt = when + 0.4;
  const chord = [D3, D3 + 12, D3 + 19, D3 + 28];
  for (let i = 0; i < chord.length; i++) {
    struck(bus, {
      when: chordAt + i * 0.016,
      midi: chord[i]!,
      level: (0.16 - i * 0.017) * level,
      decay: 1.8 + i * 0.2,
      wave,
      send: 0.46,
      pan: pan + (i - 1.5) * 0.1,
      tilt: 4,
    });
  }

  noiseBurst(bus, {
    kind: 'white',
    when: chordAt,
    attack: 0.24,
    decay: 1.3,
    level: 0.047 * level,
    group: BONUS,
    pan,
    send: 0.6,
    filter: { type: 'highpass', freq: 6200, q: 0.5 },
  });
}

/* ----------------------------------------------------------------------- ui -- */

/** Pointer entering something interactive. Barely there by design. */
export function uiHover(bus: SynthBus, o: CueOptions = {}): void {
  tone(bus, {
    freq: mtof(D5 + 24),
    type: 'sine',
    attack: 0.003,
    decay: 0.035,
    level: 0.06 * (o.level ?? 1),
    when: o.when ?? bus.now(),
    group: UI,
    pan: o.pan ?? 0,
    send: 0.06,
  });
}

/** Confirming press. A shade lower than hover, with a click of contact. */
export function uiPress(bus: SynthBus, o: CueOptions = {}): void {
  const when = o.when ?? bus.now();
  const level = o.level ?? 1;
  const pan = o.pan ?? 0;

  tone(bus, {
    freq: mtof(D5 + 12),
    type: 'sine',
    glideTo: mtof(D5 + 19),
    glideTime: 0.03,
    attack: 0.002,
    decay: 0.055,
    level: 0.09 * level,
    when,
    group: UI,
    pan,
    send: 0.1,
  });

  noiseBurst(bus, {
    kind: 'white',
    when,
    attack: 0.001,
    decay: 0.014,
    level: 0.045 * level,
    group: UI,
    pan,
    filter: { type: 'bandpass', freq: 3200, q: 1.6 },
  });
}

/* -------------------------------------------------------------- transitions -- */

/** New round: a short riffle of the deck, then the stack tapped square. */
export function shuffle(bus: SynthBus, o: CueOptions = {}): void {
  const when = o.when ?? bus.now();
  const level = o.level ?? 1;

  for (let i = 0; i < 7; i++) {
    noiseBurst(bus, {
      kind: 'pink',
      when: when + i * 0.038 + rand(-0.006, 0.006),
      attack: 0.002,
      decay: rand(0.03, 0.055),
      level: (0.07 + i * 0.005) * level,
      rate: rand(0.85, 1.2),
      group: CARD,
      pan: rand(-0.3, 0.3),
      send: 0.16,
      filter: { type: 'bandpass', freq: rand(1200, 2200), q: 1.1 },
      filter2: { type: 'highpass', freq: 420, q: 0.6 },
    });
  }

  cardLand(bus, { when: when + 0.31, level: 0.6 * level });
}

/** Taking a move back. A reversed-sounding sweep — it rises, then stops dead. */
export function undo(bus: SynthBus, o: CueOptions = {}): void {
  const when = o.when ?? bus.now();
  const level = o.level ?? 1;
  const pan = o.pan ?? 0;

  noiseBurst(bus, {
    kind: 'pink',
    when,
    attack: 0.14,
    decay: 0.025,
    level: 0.14 * level,
    group: UI,
    pan,
    send: 0.16,
    filter: { type: 'bandpass', freq: 520, q: 1.1, sweepTo: 2600, sweepTime: 0.16 },
  });

  tone(bus, {
    freq: mtof(D5),
    type: 'sine',
    glideTo: mtof(D5 - 5),
    glideTime: 0.1,
    attack: 0.005,
    decay: 0.16,
    level: 0.1 * level,
    when: when + 0.15,
    group: UI,
    pan,
    send: 0.2,
  });
}
